<template>
  <PlayerLayout>
    <div class="mx-12 my-6" slot="main">
      <v-sheet class="pa-6" rounded>
        <div class="d-flex flex-row align-center justify-space-between">
          <h1 class="outfit-regular">{{ conundrum?.title || conundrum?.name }}</h1>
          <div class="d-flex flex-row align-center">
            <v-btn icon :color="liked ? 'red' : ''" :disabled="!authUser" @click="toggleLike">
              <v-icon>{{ liked ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}</v-icon>
            </v-btn>
            <span class="ml-2">{{ likeCount }}</span>
            <v-btn v-if="authUser && canEdit" class="ml-4" small color="teal" dark @click="edit">Edit</v-btn>
          </div>
        </div>
        <div class="grey--text text--darken-1 mb-4">{{ conundrum?.description }}</div>

        <div class="mb-6">
          <div class="cal-sans-regular font-size-20 mb-2">{{ conundrum?.prompt }}</div>
          <div class="d-flex flex-row flex-wrap">
            <v-btn v-for="o in conundrum?.options || []" :key="o.id" class="ma-2" color="primary" outlined :disabled="!authUser" @click="vote(o.id)">
              <v-icon left>mdi-vote</v-icon>
              {{ o.text }}
              <span v-if="votes[o.id] !== undefined" class="ml-2">({{ votes[o.id] }})</span>
            </v-btn>
          </div>
          <div v-if="authUser && userVote" class="mt-2 grey--text">You voted: {{ displayOption(userVote) }}</div>
        </div>

        <v-divider></v-divider>

        <div class="mt-6">
          <div class="cal-sans-regular font-size-20 mb-2">Comments</div>
          <div v-if="authUser" class="d-flex flex-row mb-4">
            <v-text-field class="flex-grow-1 mr-2" outlined dense v-model="commentText" label="Add a comment"></v-text-field>
            <v-btn color="teal" dark class="elevation-0" :disabled="!commentText.trim()" @click="postComment">
              <v-icon left>mdi-send</v-icon>
              Post
            </v-btn>
          </div>
          <div>
            <v-list two-line>
              <v-list-item v-for="c in comments" :key="c.id">
                <v-list-item-avatar>
                  <v-icon>mdi-account</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ c.creator }}</v-list-item-title>
                  <v-list-item-subtitle>{{ c.text }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="authUser && c.creator === authUser.uid">
                  <v-btn icon small @click="deleteComment(c.id)"><v-icon>mdi-delete</v-icon></v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </div>
      </v-sheet>
    </div>
  </PlayerLayout>
</template>

<script>
import { doc, getDoc, collection, getDocs, setDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase/init'
import PlayerLayout from '@/layouts/Player.vue'

export default {
  name: 'ConundrumPlayer',

  components: { PlayerLayout },

  data() {
    return {
      conundrumId: null,
      conundrum: null,
      loading: false,
      liked: false,
      likeCount: 0,
      votes: {},
      userVote: null,
      comments: [],
      commentText: ''
    }
  },

  computed: {
    canEdit() {
      return this.conundrum && this.authUser && this.conundrum.creator && this.conundrum.creator.id === this.authUser.uid
    }
  },

  created() {
    this.initialize()
  },

  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newId, oldId) {
        if (newId !== oldId) {
          this.initialize()
        }
      }
    }
  },

  methods: {
    async initialize() {
      this.loading = true
      this.conundrumId = this.$route.params.id
      if (!this.conundrumId) {
        this.loading = false
        return
      }
      await this.loadConundrum()
      await this.loadLikes()
      await this.loadVotes()
      this.subscribeComments()
      this.loading = false
    },

    async loadConundrum() {
      const ref = doc(db, 'conundrums', this.conundrumId)
      const snap = await getDoc(ref)
      if (snap.exists()) {
        const data = snap.data()
        data.id = snap.id
        this.conundrum = data
      } else {
        this.conundrum = null
      }
    },

    async loadLikes() {
      const likesRef = collection(db, 'conundrums', this.conundrumId, 'likes')
      const snapshot = await getDocs(likesRef)
      this.likeCount = snapshot.size
      if (this.authUser) {
        const myLikeRef = doc(db, 'conundrums', this.conundrumId, 'likes', this.authUser.uid)
        const myLike = await getDoc(myLikeRef)
        this.liked = myLike.exists()
      }
    },

    async toggleLike() {
      if (!this.authUser) return
      const myLikeRef = doc(db, 'conundrums', this.conundrumId, 'likes', this.authUser.uid)
      const myLike = await getDoc(myLikeRef)
      if (myLike.exists()) {
        await deleteDoc(myLikeRef)
        this.liked = false
        this.likeCount = Math.max(0, this.likeCount - 1)
      } else {
        await setDoc(myLikeRef, { userId: this.authUser.uid, createdAt: new Date() })
        this.liked = true
        this.likeCount += 1
      }
    },

    async loadVotes() {
      const votesRef = collection(db, 'conundrums', this.conundrumId, 'votes')
      const snapshot = await getDocs(votesRef)
      const counts = {}
      snapshot.forEach(d => {
        const v = d.data()
        counts[v.optionId] = (counts[v.optionId] || 0) + 1
      })
      this.votes = counts
      if (this.authUser) {
        const myVoteRef = doc(db, 'conundrums', this.conundrumId, 'votes', this.authUser.uid)
        const myVote = await getDoc(myVoteRef)
        this.userVote = myVote.exists() ? myVote.data().optionId : null
      }
    },

    async vote(optionId) {
      if (!this.authUser) return
      const myVoteRef = doc(db, 'conundrums', this.conundrumId, 'votes', this.authUser.uid)
      await setDoc(myVoteRef, { userId: this.authUser.uid, optionId, createdAt: new Date() })
      await this.loadVotes()
    },

    subscribeComments() {
      const commentsRef = collection(db, 'conundrums', this.conundrumId, 'comments')
      onSnapshot(commentsRef, (snapshot) => {
        this.comments = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
      })
    },

    async postComment() {
      if (!this.authUser || !this.commentText.trim()) return
      const commentsRef = collection(db, 'conundrums', this.conundrumId, 'comments')
      await setDoc(doc(commentsRef), { creator: this.authUser.uid, text: this.commentText.trim(), createdAt: new Date() })
      this.commentText = ''
    },

    async deleteComment(id) {
      const ref = doc(db, 'conundrums', this.conundrumId, 'comments', id)
      await deleteDoc(ref)
    },

    displayOption(optionId) {
      const opt = (this.conundrum?.options || []).find(o => o.id === optionId)
      return opt ? opt.text : optionId
    },

    edit() {
      this.$creator.edit('conundrum', this.conundrum.id)
    }
  }
}
</script> 